/**
 * Estado de selecao do modal SeletorConjuntos.
 *
 * Espelha `useSelecaoUsinas.ts` em estrutura — `Set<number>` em `useRef`
 * + nonce via `useReducer` para invalidar memos. Justificativa detalhada
 * naquele hook (toggle <16ms mesmo em CPU lenta).
 *
 * Diferenca para o de usinas: em vez de `totalPotencia(usinasPorId)`,
 * expomos `totalAgregado(conjuntosPorId)` — devolve `{ totalUsinas,
 * totalPotenciaMw }` numa unica passada do Set. O Resumo do modal precisa
 * dos dois numeros simultaneamente; calcular separado seria 2 iteracoes.
 */
import {
  useCallback, useMemo, useReducer, useRef,
} from 'react';
import { ConjuntoAgregado } from 'helper/windx';

interface UseSelecaoConjuntosParams {
  inicial?: number[];
}

export interface TotalAgregadoSelecao {
  totalUsinas: number;
  totalPotenciaMw: number;
}

export interface UseSelecaoConjuntosReturn {
  isSelecionado: (id: number) => boolean;
  toggle: (id: number) => void;
  selecionarTodos: (ids: number[]) => void;
  desselecionarTodos: (ids: number[]) => void;
  limpar: () => void;
  resetar: (ids: number[]) => void;
  count: number;
  totalAgregado: (
    conjuntosPorId: Map<number, ConjuntoAgregado>,
  ) => TotalAgregadoSelecao;
  ids: number[];
}

export default function useSelecaoConjuntos(
  { inicial = [] }: UseSelecaoConjuntosParams = {},
): UseSelecaoConjuntosReturn {
  const setRef = useRef<Set<number>>(new Set(inicial));
  const [nonce, forceUpdate] = useReducer((x: number) => x + 1, 0);

  const isSelecionado = useCallback((id: number) => setRef.current.has(id), []);

  const toggle = useCallback((id: number) => {
    if (setRef.current.has(id)) {
      setRef.current.delete(id);
    } else {
      setRef.current.add(id);
    }
    forceUpdate();
  }, []);

  const selecionarTodos = useCallback((ids: number[]) => {
    ids.forEach((id) => setRef.current.add(id));
    forceUpdate();
  }, []);

  const desselecionarTodos = useCallback((ids: number[]) => {
    ids.forEach((id) => setRef.current.delete(id));
    forceUpdate();
  }, []);

  const limpar = useCallback(() => {
    setRef.current = new Set();
    forceUpdate();
  }, []);

  const resetar = useCallback((ids: number[]) => {
    setRef.current = new Set(ids);
    forceUpdate();
  }, []);

  const count = setRef.current.size;

  const totalAgregado = useCallback(
    (conjuntosPorId: Map<number, ConjuntoAgregado>): TotalAgregadoSelecao => {
      let totalUsinas = 0;
      let totalPotenciaMw = 0;
      setRef.current.forEach((id) => {
        const c = conjuntosPorId.get(id);
        if (c) {
          totalUsinas += c.qtd_usinas;
          totalPotenciaMw += c.potencia_total_mw;
        }
      });
      return { totalUsinas, totalPotenciaMw };
    },
    [],
  );

  const ids = useMemo(
    () => Array.from(setRef.current).sort((a, b) => a - b),
    // `nonce` e a unica dep confiavel — `count` falha quando `resetar`
    // substitui um set de N itens por outro de N itens distintos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nonce],
  );

  return {
    isSelecionado,
    toggle,
    selecionarTodos,
    desselecionarTodos,
    limpar,
    resetar,
    count,
    totalAgregado,
    ids,
  };
}
